import { useState } from 'react';
import type { ConsumerOffer } from '../types/dtos';
import { DatePicker } from './DatePicker';
import { DropOffAuthorizationOption } from './DropOffAuthorizationOption';
import { ExtrasPicker } from './ExtrasPicker';
import { FeedbackWidget } from './FeedbackWidget';
import { HelpButton } from './HelpButton';
import { HintBadge } from './HintBadge';
import { LanguageToggle } from './LanguageToggle';
import { TenantLogo } from './TenantLogo';
import { useT } from '../hooks/useT';

type SubmitPayload = {
  deliveryDateId: string | null;
  selectedExtraIds: string[];
  dropOff: { locationOptionId: string | null; note: string | null; termsAccepted: boolean } | null;
};

type Props = {
  offer: ConsumerOffer;
  now: Date;
  submitting: boolean;
  banner?: string | null;
  token: string;
  onSubmit: (payload: SubmitPayload) => void;
};

export function OfferView({ offer, now, submitting, banner, token, onSubmit }: Props) {
  const { t, locale } = useT();
  const dropOffConfig = offer.dropOffAuthorization ?? null;

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const [dropOffSelected, setDropOffSelected] = useState(false);
  const [dropOffLocation, setDropOffLocation] = useState<string | null>(null);
  const [dropOffNote, setDropOffNote] = useState('');
  const [dropOffTerms, setDropOffTerms] = useState(false);

  const chooseDate = (id: string) => { setSelectedDate(id); setDropOffSelected(false); };
  const chooseDropOff = () => { setDropOffSelected(true); setSelectedDate(null); };

  // ISSUE-0137: Sendungsinfo rows — number, sender (addressDisplay, falling back to name), recipient.
  const senderDisplay = offer.senderAddressDisplay ?? offer.senderName ?? null;
  const shipmentInfoRows: { label: string; value: string }[] = [
    ...(offer.externalShipmentId ? [{ label: t('offer.shipment_number'), value: offer.externalShipmentId }] : []),
    ...(senderDisplay ? [{ label: t('offer.shipment_sender'), value: senderDisplay }] : []),
    ...(offer.consigneeAddressDisplay
      ? [{ label: t('offer.shipment_recipient'), value: offer.consigneeAddressDisplay }]
      : []),
    // ISSUE-0195: order positions from AdditionalData; omitted entirely when not supplied.
    ...(offer.shipmentDetails
      ? [{ label: t('offer.shipment_details'), value: offer.shipmentDetails }]
      : []),
  ];

  const canSubmit =
    !submitting &&
    ((selectedDate !== null) || (dropOffSelected && dropOffTerms));

  const submit = () => {
    if (dropOffSelected) {
      onSubmit({
        deliveryDateId: null,
        selectedExtraIds: [],
        dropOff: {
          locationOptionId: dropOffLocation,
          note: dropOffNote.trim() ? dropOffNote.trim() : null,
          termsAccepted: dropOffTerms,
        },
      });
    } else if (selectedDate) {
      onSubmit({ deliveryDateId: selectedDate, selectedExtraIds: selectedExtras, dropOff: null });
    }
  };

  return (
    <main className="mx-auto max-w-2xl px-4 py-8">
      <TenantLogo logoUrl={offer.branding?.logoUrl} />
      <div className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-slate-900">{t('offer.title')}</h1>
        <div className="flex items-center gap-2">
          <LanguageToggle compact />
          <HelpButton token={token} />
        </div>
      </div>

      {banner && (
        <div role="alert" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {banner}
        </div>
      )}

      {shipmentInfoRows.length > 0 && (
        // ISSUE-0137: styled like the delivery-date selection block below (same heading + card),
        // with aligned label/value rows (Sendungsnummer / Absender / Empfänger).
        <section aria-label={t('offer.shipment_info_title')} className="mb-6">
          <h2 className="mb-2 text-base font-medium text-slate-900">{t('offer.shipment_info_title')}</h2>
          <dl className="space-y-2 rounded-lg border border-slate-300 bg-white p-3 text-sm">
            {shipmentInfoRows.map((row) => (
              <div key={row.label} className="flex gap-3">
                <dt className="w-32 shrink-0 font-medium text-slate-500">{row.label}</dt>
                <dd className="flex-1 whitespace-pre-line text-slate-900">{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {offer.hints.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {offer.hints.map((h) => (
            <HintBadge key={h.code} code={h.code} label={h.label} />
          ))}
        </div>
      )}

      <DatePicker
        dates={offer.deliveryDates}
        now={now}
        selectedId={selectedDate}
        onChange={chooseDate}
        locale={locale}
      />

      {dropOffConfig && (
        <DropOffAuthorizationOption
          config={dropOffConfig}
          selected={dropOffSelected}
          locationOptionId={dropOffLocation}
          note={dropOffNote}
          termsAccepted={dropOffTerms}
          onSelect={chooseDropOff}
          onLocationChange={setDropOffLocation}
          onNoteChange={setDropOffNote}
          onTermsChange={setDropOffTerms}
        />
      )}

      {!dropOffSelected && (
        <ExtrasPicker extras={offer.extraServicesOffered} selected={selectedExtras} onChange={setSelectedExtras} />
      )}

      <div className="mt-8 flex items-center justify-end">
        <button
          type="button"
          disabled={!canSubmit}
          aria-busy={submitting}
          onClick={submit}
          className="rounded-lg bg-brand px-6 py-3 text-base font-medium text-white shadow-sm transition hover:bg-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-ring disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {submitting ? t('offer.submitting') : t('offer.submit')}
        </button>
      </div>

      <FeedbackWidget token={token} adviceJobId={offer.adviceJobId} mode={offer.feedbackMode} />
    </main>
  );
}
